import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const POLL_INTERVAL_MS = 60_000;

/**
 * Tiny dot in the top nav that polls /api/health/deep every 60s.
 * - green : status === 'ok'
 * - amber : status === 'degraded'
 * - red   : fetch error / non-200
 * - gray  : initial check in progress
 *
 * Super admins can click the badge to jump to /admin/status.
 * Non-admins still see the dot for at-a-glance reassurance, but the
 * click-through is a no-op (the page is gated server-side anyway).
 */
export default function HealthStatusBadge() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState('checking'); // 'ok' | 'degraded' | 'error' | 'checking'
  const [detail, setDetail] = useState(null);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/health/deep', {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) {
        setState('error');
        setDetail({ message: `HTTP ${res.status}` });
        return;
      }
      const data = await res.json();
      setState(data.status === 'ok' ? 'ok' : 'degraded');
      setDetail(data);
    } catch (e) {
      setState('error');
      setDetail({ message: e.message || 'Network error' });
    }
  }, []);

  useEffect(() => {
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [poll]);

  const isSuperAdmin = user?.role === 'super_admin';

  const colors = {
    ok: { dot: 'bg-emerald-500', glow: 'shadow-[0_0_8px_rgba(16,185,129,0.6)]', label: 'All systems operational' },
    degraded: { dot: 'bg-amber-500', glow: 'shadow-[0_0_8px_rgba(245,158,11,0.6)]', label: 'Some systems degraded' },
    error: { dot: 'bg-red-500', glow: 'shadow-[0_0_8px_rgba(239,68,68,0.6)]', label: 'Health check failed' },
    checking: { dot: 'bg-slate-400', glow: '', label: 'Checking platform health…' },
  };
  const c = colors[state];

  // Build a compact list of failing checks for the tooltip.
  const failingChecks = (() => {
    if (!detail?.checks) return [];
    const informational = new Set(['sentry', 'email']);
    return Object.entries(detail.checks)
      .filter(([k, v]) => !informational.has(k) && v && v.ok === false)
      .map(([k]) => k);
  })();

  const onClick = () => {
    if (isSuperAdmin) navigate('/admin/status');
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="health-status-badge-wrapper"
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={c.label}
        title={c.label}
        className={`flex items-center justify-center w-8 h-8 rounded-xl text-muted-foreground hover:bg-muted/40 transition-colors ${isSuperAdmin ? 'cursor-pointer' : 'cursor-default'}`}
        data-testid="health-status-badge"
        data-status={state}
      >
        <span className="relative flex items-center justify-center">
          {state === 'ok' && (
            <span className={`absolute w-3 h-3 rounded-full ${c.dot} opacity-40 animate-ping`} />
          )}
          <span className={`relative w-2.5 h-2.5 rounded-full ${c.dot} ${c.glow}`} />
        </span>
      </button>

      {hovered && (
        <div
          className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-border/50 bg-popover/95 backdrop-blur-xl shadow-xl p-3 z-[200] text-left"
          data-testid="health-status-tooltip"
          style={{ background: 'var(--glass-bg, rgba(20,24,40,0.95))' }}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full ${c.dot}`} />
            <span className="text-xs font-semibold text-foreground">{c.label}</span>
          </div>
          {state === 'degraded' && failingChecks.length > 0 && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Affected: <span className="text-amber-400 font-medium">{failingChecks.join(', ')}</span>
            </p>
          )}
          {state === 'error' && detail?.message && (
            <p className="text-[10px] text-muted-foreground mt-1">{detail.message}</p>
          )}
          {detail?.elapsed_ms != null && state !== 'error' && (
            <p className="text-[10px] text-muted-foreground/70 mt-1">
              Probed in {detail.elapsed_ms}ms
            </p>
          )}
          {isSuperAdmin ? (
            <p className="text-[10px] text-primary/80 mt-2">Click for full status →</p>
          ) : (
            <p className="text-[10px] text-muted-foreground/80 mt-2">Polled every 60s</p>
          )}
        </div>
      )}
    </div>
  );
}
